import Vue from 'vue';
import {
    getSessionReplayEntities,
    getStarReplay,
    createSessionReplayEntity,
    starReplay,
    unstarReplay,
    updateReplayEntity,
    getSavedClips,
    getSavedClipContext,
    watchClip,
    deleteClip,
    updateSavedClipDescription,
} from './session-replay.utils';
import { getEntityKey, getReplaySettings, runReplayRecordingAggregation } from '@/session-replay/helpers/common.util';
import values from 'lodash/values';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import get from 'lodash/get';

let loadAllPromise;

export default {
    namespaced: true,
    state: {
        error: null,
        fetching: false,
        sessionReplayEntities: {},
        savedClips: {},
        appReplaySettings: {},
    },
    mutations: {
        setMap (state, { mapName, value }) {
            state[mapName] = value;
        },
        setMapAtKey (state, { mapName, key, value }) {
            Vue.set(state[mapName], key, value);
        },
        updateMapAttributesAtKey (state, { mapName, key, attributes }) {
            attributes.forEach((attribute) => {
                Vue.set(state[mapName][key], attribute.key, attribute.value);
            });
        },
        deleteMapKey (state, { mapName, key }) {
            Vue.delete(state[mapName], key);
        },
        setError (state, { error }) {
            state.error = error;
        },
        setFetching (state, { fetching }) {
            state.fetching = fetching;
        },
    },
    actions: {
        async loadAll ({ commit, getters }, { reFetch = false } = {}) {
            if (loadAllPromise && !reFetch) {
                await loadAllPromise;

                return;
            }

            if (getters.hasReplayEntities && !reFetch) {
                return;
            }

            commit('setFetching', { fetching: true });
            loadAllPromise = getSessionReplayEntities();

            try {
                const replays = await loadAllPromise;

                commit('setFetching', { fetching: false });
                commit('setMap', {
                    mapName: 'sessionReplayEntities',
                    value: replays,
                });
            } catch (error) {
                loadAllPromise = null;
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        updateEntityAtKey ({ commit }, { key, value }) {
            commit('setMapAtKey', {
                mapName: 'sessionReplayEntities',
                key,
                value,
            });
        },
        async createSessionReplayEntity ({ commit, rootGetters }, { replayBody }) {
            commit('setFetching', { fetching: true });

            if (!replayBody.recordingIds) {
                const replayRetentionDays = rootGetters['apps/getAppById'](replayBody.appId)?.recordingRetentionDays;
                replayBody = await runReplayRecordingAggregation(replayBody, replayRetentionDays);
            }
            try {
                const value = await createSessionReplayEntity(replayBody);
                commit('setMapAtKey', {
                    mapName: 'sessionReplayEntities',
                    key: getEntityKey(replayBody),
                    value,
                });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setFetching', { fetching: false });
            }
        },
        async watchedReplay ({ commit, rootGetters }, { replayBody }) {
            commit('setFetching', { fetching: true });

            if (!replayBody.recordingIds) {
                const replayRetentionDays = rootGetters['apps/getAppById'](replayBody.appId)?.recordingRetentionDays;
                replayBody = await runReplayRecordingAggregation(replayBody, replayRetentionDays);
            }

            let updatedReplay = replayBody;
            await updateReplayEntity(replayBody)
                .then((res) => {
                    commit('setFetching', { fetching: false });
                    commit('setMapAtKey', {
                        mapName: 'sessionReplayEntities',
                        key: getEntityKey(replayBody),
                        value: res,
                    });

                    if (replayBody.recordingSessionId) {
                        commit('setMapAtKey', {
                            mapName: 'sessionReplayEntities',
                            key: replayBody.recordingSessionId,
                            value: res,
                        });
                    }

                    updatedReplay = res;
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });

            return updatedReplay;
        },
        async starReplay ({ getters, commit, dispatch }, { replayBody }) {
            commit('setFetching', { fetching: true });
            let replayEntity = getters.getReplayEntityAtKey(replayBody?.recordingSessionId);
            if (!replayEntity) {
                await dispatch('createSessionReplayEntity', { replayBody });
                const newReplayEntity = getters.getReplayEntityAtKey(replayBody?.recordingSessionId);
                replayEntity = newReplayEntity;
            }
            try {
                await starReplay(replayEntity.id);
                commit('updateMapAttributesAtKey', {
                    mapName: 'sessionReplayEntities',
                    key: getEntityKey(replayBody),
                    attributes: [{ key: 'starred', value: true }],
                });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setFetching', { fetching: false });
            }
        },
        async unstarReplay ({ commit }, { replay }) {
            const replayId = replay.id;
            commit('setFetching', { fetching: true });

            await unstarReplay(replayId)
                .then(() => {
                    commit('setFetching', { fetching: false });
                    commit('updateMapAttributesAtKey', {
                        mapName: 'sessionReplayEntities',
                        key: getEntityKey(replay),
                        attributes: [{ key: 'starred', value: false }],
                    });
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        async getStarReplay ({ commit }, { replayId }) {
            commit('setFetching', { fetching: true });

            await getStarReplay(replayId)
                .then((starredStatus) => {
                    commit('setFetching', { fetching: false });

                    return starredStatus;
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        async loadSavedClips ({ commit, getters }, { reFetch = false, createdByMe = true } = {}) {
            if (!reFetch && !isEmpty(getters.savedClipsList)) {
                return;
            }

            commit('setFetching', { fetching: true });
            await getSavedClips(createdByMe)
                .then((clips) => {
                    commit('setMap', {
                        mapName: 'savedClips',
                        value: keyBy(clips, 'id'),
                    });
                })
                .catch((error) => {
                    commit('setError', { error });
                    throw error;
                })
                .finally(() => {
                    commit('setFetching', { fetching: false });
                });
        },
        async loadSavedClip ({ commit, getters }, { id, reFetch = false, abortSignal } = {}) {
            if (!reFetch && getters.getClipById(id)) {
                return;
            }

            commit('setFetching', { fetching: true });
            await getSavedClipContext(id, abortSignal)
                .then((clip) => {
                    commit('setMapAtKey', {
                        mapName: 'savedClips',
                        key: id,
                        value: clip,
                    });
                })
                .catch((error) => {
                    commit('setError', { error });
                    throw error;
                })
                .finally(() => {
                    commit('setFetching', { fetching: false });
                });
        },
        async watchSavedClip ({ commit }, { id }) {
            commit('setFetching', { fetching: true });
            await watchClip(id)
                .then((clip) => {
                    commit('setMapAtKey', {
                        mapName: 'savedClips',
                        key: id,
                        value: clip,
                    });
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                })
                .finally(() => {
                    commit('setFetching', { fetching: false });
                });
        },
        async deleteSavedClip ({ commit }, { id }) {
            commit('setFetching', { fetching: true });
            await deleteClip(id)
                .then(() => {
                    commit('deleteMapKey', {
                        mapName: 'savedClips',
                        key: id,
                    });
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                })
                .finally(() => {
                    commit('setFetching', { fetching: false });
                });
        },
        async updateSavedClipDescription ({ commit }, { id, description }) {
            commit('setFetching', { fetching: true });
            await updateSavedClipDescription({ clipId: id, description })
                .then((clip) => {
                    commit('setMapAtKey', {
                        mapName: 'savedClips',
                        key: id,
                        value: clip,
                    });
                })
                .catch((error) => {
                    commit('setError', { error });
                    throw error;
                })
                .finally(() => {
                    commit('setFetching', { fetching: false });
                });
        },
        async loadReplaySettingsForApp ({ state, commit }, { appId, reFetch }) {
            if (reFetch || !state.appReplaySettings[appId]) {
                const replaySettings = await getReplaySettings({ appId });
                commit('setMapAtKey', {
                    mapName: 'appReplaySettings',
                    key: appId,
                    value: replaySettings,
                });
            }

            return state.appReplaySettings[appId];
        },
        async handleStarChange ({ dispatch, getters }, replayBody) {
            // If replay is set to starred, star the replay on the BE
            if (get(replayBody, 'starred', false)) {
                try {
                    await dispatch('starReplay', { replayBody });

                    const replayEntity = getters.getReplayEntityAtKey(replayBody?.recordingSessionId);

                    if (!replayEntity || !replayEntity.id) {
                        throw new Error();
                    }

                    return {
                        ...replayBody,
                        id: replayEntity.id,
                    };
                } catch (error) {
                    return { ...replayBody, starred: false, errorType: 'star' };
                }
            }

            // If replay is not set to starred, unstar the replay on the BE
            try {
                await dispatch('unstarReplay', { replay: replayBody });

                return replayBody;
            } catch (error) {
                return { ...replayBody, starred: true, errorType: 'unstar' };
            }
        },
        async handleWatchedReplay ({ getters, dispatch }, replayBody) {
            try {
                await dispatch('watchedReplay', { replayBody });

                const { id: replayEntityId } = getters.getReplayEntityAtKey(replayBody?.recordingSessionId);

                if (!replayEntityId) {
                    throw new Error();
                }

                return {
                    ...replayBody,
                    id: replayEntityId,
                };
            } catch (error) {
                return {
                    ...replayBody,
                    watched: false,
                };
            }
        },
    },
    getters: {
        sessionReplayEntitiesMap: (state) => state.sessionReplayEntities,
        sessionReplayEntities: (state) => values(state.sessionReplayEntities),
        getReplayEntityAtKey: (state) => (key) => state.sessionReplayEntities[key],
        hasReplayEntities: (state) => !isEmpty(state.sessionReplayEntities),
        getClipById: (state) => (clipId) => state.savedClips[clipId],
        savedClipsList: (state) => values(state.savedClips),
    },
};
